import type { ReactNode } from "react";

import pluralize from "pluralize";

import {
  AudienceIcon,
  Avatar,
  Box,
  Column,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Row,
  Spinner,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";

import { useGroups } from "src/pages/projects/hooks";

/** A user group's icon, the size of a small Avatar so groups and people line up. */
export const GroupAvatar = () => (
  <Box
    boxSize={5}
    flexShrink={0}
    display="flex"
    alignItems="center"
    justifyContent="center"
    borderRadius="full"
    borderWidth="hairline"
    borderColor="base.border"
    bg="base.background"
  >
    <Box as={AudienceIcon} boxSize={3} color="text.secondary" />
  </Box>
);

type GroupMembersPopoverProps = {
  userGroupId: string;
  name: string;
  /** What shows the group, such as its avatar and name; hovering or focusing it lists the members. */
  children: ReactNode;
};

/** Lists a user group's members on hover or focus. */
export const GroupMembersPopover = ({
  userGroupId,
  name,
  children,
}: GroupMembersPopoverProps) => {
  const { groups, isLoading } = useGroups();
  const members =
    groups.find((group) => group.id === userGroupId)?.members ?? [];

  return (
    <Popover
      trigger="hover"
      placement="bottom-start"
      isLazy
      lazyBehavior="unmount"
    >
      <PopoverTrigger>
        <Row
          tabIndex={0}
          aria-label={
            isLoading
              ? name
              : `${name}, ${pluralize("member", members.length, true)}`
          }
          align="center"
          gap={2}
          minWidth={0}
          borderRadius="sm"
          _focusVisible={{ boxShadow: "outline", outline: "none" }}
        >
          {children}
        </Row>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="xs" maxHeight="60vh" overflowY="auto" p={3}>
          <Column gap={2}>
            <Text size="sm" fontWeight="medium">
              {name}
            </Text>
            {isLoading ? (
              <Spinner size="sm" />
            ) : members.length === 0 ? (
              <Text size="sm" color="text.tertiary">
                No members
              </Text>
            ) : (
              members.map((member) => (
                <Row key={member.id} align="center" gap={2} minWidth={0}>
                  <Avatar name={member.name} size="sm" hideTooltip />
                  <TextWithTooltip size="sm">{member.name}</TextWithTooltip>
                </Row>
              ))
            )}
          </Column>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
